/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link, useParams } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import SignalPopup from '../heart-rate-sessions/SignalPopup';
import UserSegmentToggle from '../common/UserSegmentToggle';
import { useUserSegment, watchFilterValue } from '../../context/UserSegmentContext';
import { formatISODateToLocal, showTimeInSec, showTimeFromSeconds } from '../../utils/utils';
import ExportButton from '../active-users/ExportButton';
import DateRangeBar, { fmt } from '../active-users/DateRangeBar';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import PauseHistorySummaryCards from './PauseHistorySummaryCards';
import '../active-users/active-users.css';

const LIMIT = 20;
const EXPORT_COLUMNS = [
  { key: 'id', label: 'No' },
  { key: 'name', label: 'Name' },
  { key: 'meditation_perform_time', label: 'Perform time (ms)' },
  { key: 'meditation_time', label: 'Meditation time (s)' },
  { key: 'meditation_type', label: 'Meditation type' },
  { key: 'timestamp_utc', label: 'Created' },
];

const MeditationList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = id ? atob(id) : '';
  const { segment } = useUserSegment();                 // global watch/without/all (feature 023)
  const userSegment = watchFilterValue(segment);        // 'with_watch' | 'without_watch' | ''

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFrom, setDisplayFrom] = useState(null);
  const [displayTo, setDisplayTo] = useState(null);
  const [preset, setPreset] = useState(null);

  const [meditationTypes, setMeditationTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupData, setPopupData] = useState(null);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
    getTypes();
  }, []);

  useEffect(() => {
    if (token) loadData(1, fromDate, toDate, selectedType);
  }, [token]);

  // Re-fetch when the global watch segment changes (skip initial mount).
  const didMountSeg = useRef(false);
  useEffect(() => {
    if (!didMountSeg.current) { didMountSeg.current = true; return; }
    if (!token) return;
    setPage(1);
    loadData(1, fromDate, toDate, selectedType);
  }, [segment]);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const scope = () => (userId ? `&user_id=${userId}` : '');
  const seg = () => (userSegment ? `&user_segment=${userSegment}` : '');

  const getTypes = async () => {
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/meditation-types`, auth());
      const raw = res?.data?.data?.[0]?.meditation_types;
      if (raw) setMeditationTypes(JSON.parse(raw));
    } catch { /* non-blocking: the dropdown simply stays empty */ }
  };

  const loadData = async (p, f, t, type) => {
    setLoading(true); setError('');
    const filters = `from_date=${f}&to_date=${t}&meditation_type=${type}${scope()}${seg()}`;
    try {
      const [list, sum] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/pauses?page=${p}&limit=${LIMIT}&${filters}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/pauses-summary?${filters}`, auth()),
      ]);
      setRows(list.data.data || []);
      setTotalItem(list.data.totalItem || 0);
      setSummary(sum.data.data || null);
    } catch (err) {
      if (err?.response?.status === 401) setError('auth'); else setError('load');
    } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    loadData(pageNumber, fromDate, toDate, selectedType);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFrom(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayTo(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFrom(fromStr ? new Date(fromStr) : null); setDisplayTo(toStr ? new Date(toStr) : null);
    setPage(1); loadData(1, fromStr, toStr, selectedType);
  };
  const handleSearch = () => { setPage(1); loadData(1, fromDate, toDate, selectedType); };
  const handleTypeChange = (e) => { const v = e.target.value; setSelectedType(v); setPage(1); loadData(1, fromDate, toDate, v); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFrom(null); setDisplayTo(null);
    setPreset(null); setSelectedType(''); setPage(1);
    loadData(1, '', '', '');
  };

  const openSignal = (row) => { setPopupData(row.dump_hr_details); setIsPopupOpen(true); };
  const closePopup = () => { setIsPopupOpen(false); setPopupData(null); };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/pauses-export?from_date=${fromDate}&to_date=${toDate}&meditation_type=${selectedType}${scope()}${seg()}`,
      auth());
    return res.data.data || [];
  };

  const hasFilters = !!(selectedType || fromDate || toDate);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  // avg_duration averages meditation_time, which is stored in seconds.
  const dur = (v) => (v == null ? '—' : showTimeFromSeconds(v));

  const summaryCards = summary ? [
    { label: 'Total Meditation', value: Number(summary.total_sessions || 0).toLocaleString() },
    { label: 'Avg session duration', value: dur(summary.avg_duration) },
    { label: 'Distinct active days', value: Number(summary.active_days || 0).toLocaleString() },
  ] : [];

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › Pauses › <b>Meditation history</b></span>
            <span className="au-route">/pauses-list</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/user-pause-count">← Back</Link></span>
          </div>
          <h1 className="au-title">Meditation history</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the meditation history. Please try again." onRetry={() => loadData(page, fromDate, toDate, selectedType)} />
          ) : (
            <>
              <PauseHistorySummaryCards cards={summaryCards} />

              <DateRangeBar
                leading={(
                  <select className="au-select" value={selectedType} onChange={handleTypeChange} aria-label="Filter by meditation technique">
                    <option value="">All techniques</option>
                    {meditationTypes.map((t, i) => <option key={i} value={t}>{t}</option>)}
                  </select>
                )}
                preset={preset}
                displayFrom={displayFrom}
                displayTo={displayTo}
                hasRange={hasFilters}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="pause-range-note">
                The date range filters records by their <b>activity date (UTC)</b> — the “Created” column (the moment each meditation was recorded).{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all records (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No records in this range" hasFilters={hasFilters} onClear={clearFilter}
                  message="No meditation records match the current filters." />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Meditation records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="meditation-history.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table au-mau-table" data-testid="meditation-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Perform time</th><th>Meditation time</th>
                          <th>Meditation type</th><th>Created</th><th>Signal Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id}>
                            <td data-label="No" className="tnum">{r.id}</td>
                            <td data-label="Name" className="au-ellipsis">{r.name}</td>
                            <td data-label="Perform time" className="tnum">{showTimeInSec(r.meditation_perform_time)}</td>
                            <td data-label="Meditation time" className="tnum">{showTimeFromSeconds(r.meditation_time)}</td>
                            <td data-label="Meditation type">{r.meditation_type}</td>
                            <td data-label="Created">{formatISODateToLocal(r.timestamp_utc)}</td>
                            <td data-label="Signal Details">
                              <button type="button" className="au-link" aria-label={`View signal details for record ${r.id}`} onClick={() => openSignal(r)}>
                                <img src={`${process.env.PUBLIC_URL}/images/view.png`} alt="" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}

              {isPopupOpen && <SignalPopup data={popupData} onClose={closePopup} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeditationList;
